import { GraphQLError } from "graphql"
import { createTranslator } from "short-uuid"

import {
  checkIfActionAllowed,
  checkIfBranchesAllowed,
  noAlternativeShippingAddress,
  shouldSavePicklistToggle,
  validAlternativeShippingAddress,
} from "@/graphql/schemas/order/utils/orderHelpers"
import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { getCart } from "../../cart/utils/getCart"
import { checkIfCustomerIsInternal } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, isIdmUser } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getCustomer } from "../../shared/utils/getCustomer"
import { getEmployeeReference } from "../../shared/utils/getEmployeeReference"
import { getEmployeesByNumber } from "../../shared/utils/getEmployees"
import { getUserPrintNumber } from "../../shared/utils/getUserInformation"
import { orderCreateValidation, orderDeliveryFieldsValidation } from "../validation/orderCreateValidation"

const commerceApi = CommerceService.api()

export const mutationCreateOrderFromCustomerCart = {
  async createOrderFromCustomerCart(_parent, args, context, _info) {
    let { cartId, cartKey, values } = args
    const { user } = context

    const config = AppSettings.sanityConfigByLang()
    const client = context.sanityClient
    client.config({ ...config, useCdn: true })

    await checkPermissions({
      user,
      domainObject: DomainObjects.ORDERS,
      operation: Operations.CREATE,
    })

    // Retry mechanism for race condition prevention
    let retries = 3
    let customerCart
    let lastError

    while (retries > 0) {
      try {
        customerCart = await getCart(cartId, cartKey)

        if (!customerCart) {
          throw new Error("Customer cart not found")
        }
        // Check if cart is already frozen (prevent duplicate orders)
        if (customerCart.cartState === "Frozen") {
          throw new GraphQLError("Cart is already frozen and cannot be used for checkout", {
            extensions: {
              code: "CART_ALREADY_FROZEN",
            },
          })
        }

        break // Success, exit retry loop
      } catch (error) {
        lastError = error

        // Check if it's a version conflict (409) and we have retries left
        if (error.status === 409 && retries > 1) {
          retries--
          // Wait a short time before retry
          await new Promise((resolve) => setTimeout(resolve, 100 + Math.random() * 50))
          continue
        }

        // If it's not a retryable error or we're out of retries, throw
        throw error
      }
    }

    if (!customerCart) {
      throw lastError || new Error("Customer cart not found after retries")
    }

    if (customerCart.customLineItems.length === 0) {
      throw new Error("Cart does not contain any custom line items")
    }

    const [customer, isInternalCustomer] = await Promise.all([
      getCustomer({ id: customerCart?.customerId }),
      checkIfCustomerIsInternal({ id: customerCart?.customerId }),
    ])

    if (!customer) {
      throw new Error("Customer, that the cart is assigned to not found")
    }

    checkIfBranchesAllowed(
      values?.branchId,
      customer?.custom?.fields?.deliveryBranch ?? customer?.custom?.fields?.customerBranch,
      isInternalCustomer,
    )

    const admExists = await getEmployeesByNumber(values?.admNumber, client)
    checkIfActionAllowed(values?.documentType, user?.allowReservationMutation, isInternalCustomer, admExists)

    // custom type created for custom line custom fields
    const customTypeCart = (await commerceApi.types().withKey({ key: "customCartTypeKey" }).get().execute())?.body

    if (!customTypeCart) {
      throw new Error("Custom type not found")
    }

    // this is our default shipping address
    let customerAddress = customerCart?.shippingAddress

    if (values?.addressId) {
      const isShippingAddress = customer?.shippingAddressIds?.includes(values?.addressId)

      const foundAddress = isShippingAddress
        ? customer?.addresses?.find((address) => address?.id === values.addressId)
        : null
      customerAddress = foundAddress || customerAddress
    }

    if (customer.custom.fields.customerStatus === "E") {
      if (noAlternativeShippingAddress(values.deliveryObjectValues)) {
        if (values?.addressId && customerCart.billingAddress.id === values.addressId) {
          throw new Error("Shipping address and billing address of one time customers need to be different")
        }
        if (customerCart.billingAddress.id === customerCart.shippingAddress.id && !values?.addressId) {
          throw new Error("Shipping address and billing address of one time customers need to be different")
        }
      } else if (!validAlternativeShippingAddress(values.deliveryObjectValues)) {
        throw new Error("Alternative shipping address is incomplete")
      }
    }

    const isIDMUser = isIdmUser(user?.role)
    const isOffer = values?.documentType === "offer"
    const isReservation = values?.documentType === "reservation"

    let contactPersonsInformation = customer?.addresses?.map((address) => {
      // make sure that this is contact person exists and is actuall a contact person
      if (
        values?.contactPersons?.includes(address?.id) &&
        !customer?.billingAddressIds?.includes(address?.id) &&
        !customer?.shippingAddressIds?.includes(address?.id)
      ) {
        return isOffer || isIDMUser ? address?.email : address?.id
      }
      return null
    })

    contactPersonsInformation = contactPersonsInformation.filter((n) => n)

    // validate inputs
    try {
      await orderCreateValidation({
        validTillRequired: isReservation,
        costUnitRequired: customer?.custom?.fields?.cost_unit_mandatory || false,
      }).validate(values)
      await orderDeliveryFieldsValidation().validate(values.deliveryObjectValues)
    } catch (err) {
      throw new Error(err)
    }

    const paymentTill = values.paymentTill ? new Date(values.paymentTill).toISOString().split("T")[0] : undefined
    const deliveryDate = values.deliveryDate ? new Date(values.deliveryDate).toISOString().split("T")[0] : undefined
    const validTill =
      ["offer", "reservation"].includes(values.documentType) && values.validTill
        ? new Date(values.validTill).toISOString().split("T")[0]
        : undefined

    let deliveryObjectDetails = JSON.stringify(values?.deliveryObjectValues)
    deliveryObjectDetails = deliveryObjectDetails === "{}" ? "" : deliveryObjectDetails
    const zeroTaxCategory = (await commerceApi.taxCategories().withKey({ key: "default-vat-de-0" }).get().execute())
      ?.body

    if (!zeroTaxCategory) throw new Error("Shipping method tax not found")

    if (!customerCart.custom.fields.createdByEmployeeNumber) {
      throw new Error("Missing cart user number")
    }

    const userPrintNumber = await getUserPrintNumber({ userId: user.id })
    const reservationNumber = customerCart?.custom?.fields?.reservationNumber
    const picklistNumber = customerCart?.custom?.fields?.picklistNumber

    if (picklistNumber && picklistNumber.length > 0) {
      try {
        const graphiQLQueryObject = [
          {
            method: "findOrder",
            variables: { orderNumber: picklistNumber },
            fields: ["picklist", "groupBillingKey"],
          },
        ]

        const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
        const orderResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.findOrder

        if (!orderResponse) {
          throw new GraphQLError("Source order for picklist not found", {
            extensions: {
              code: "PICKLIST_ORDER_NOT_FOUND",
            },
          })
        }

        if (orderResponse.picklist !== "true" && orderResponse.picklist !== true) {
          throw new GraphQLError("The source order is no longer a picklist. The order cannot be created.", {
            extensions: {
              code: "PICKLIST_NO_LONGER_VALID",
            },
          })
        }
      } catch (error) {
        if (error instanceof GraphQLError) {
          throw error
        }
        console.error("Error checking picklist status:", error)
        throw new GraphQLError("Failed to verify picklist status", {
          extensions: {
            code: "PICKLIST_CHECK_FAILED",
          },
        })
      }
    }

    const savePicklistToggle = shouldSavePicklistToggle(values?.shippingMethodName, values?.documentType, isIDMUser)

    const actions = [
      {
        action: "setShippingAddress",
        address: customerAddress,
      },
      {
        action: "setCustomShippingMethod",
        shippingMethodName: values.shippingMethodName,
        shippingRate: {
          price: {
            currencyCode: "EUR",
            centAmount: customerCart?.custom?.fields?.freightSurcharge?.centAmount || 0,
          },
        },
        taxCategory: { id: zeroTaxCategory.id, typeId: "tax-category" },
      },
      {
        action: "setCustomType",
        type: {
          typeId: "type",
          id: customTypeCart.id,
        },
        fields: {
          ...(userPrintNumber && { printAdmNumber: userPrintNumber }),
          freightSurcharge: customerCart?.custom?.fields?.freightSurcharge,
          createdByEmployeeNumber: customerCart.custom.fields.createdByEmployeeNumber,
          ...(reservationNumber && { reservationNumber }),
          ...(picklistNumber && { picklistNumber }),
          ...(!isReservation && {
            collectiveInvoiceType: values.collectiveInvoiceType,
            sendInfoMail: values.sendInfoMail,
            paymentTill,
            deliveryDate,
            deliveryObjectDetails,
            emailMessage: values?.emailMessage,
            emailSubject: values?.emailSubject,
            contactPersons: contactPersonsInformation?.length > 0 ? contactPersonsInformation.join(", ") : "",
          }),
          documentType: values.documentType,
          displayRecommendedSalesPrice: values.displayRecommendedSalesPrice,
          printOffer: values.printOffer,
          admNumber: values.admNumber,
          additionalInformationOne: values?.additionalInformationOne,
          additionalInformationTwo: values?.additionalInformationTwo,
          additionalInformationThree: values?.additionalInformationThree,
          validTill,
          customerExternalId: customer?.externalId,
          branchId: values.branchId,
          orderText: values?.orderText,
          orderNumber: values?.orderNumber,
          costUnit: values?.costUnit,
          ...(savePicklistToggle && { togglePicklist: values?.togglePicklist }),
        },
      },
    ]

    let sanityEntryType = "order"
    switch (values.documentType) {
      case "offer":
        sanityEntryType = "offer"
        break
      case "reservation":
        sanityEntryType = "reservation"
        break
      default:
        sanityEntryType = "order"
        break
    }

    const storeKey = customerCart?.store?.key

    const cartPayload = {
      ...(storeKey && { storeKey }),
      version: customerCart.version,
      actions: [
        ...actions,
        { action: "freezeCart" }, // Immediately freeze cart to prevent race conditions
      ],
    }

    const updatedCart = (
      await commerceApi
        .carts()
        .withId({ ID: customerCart.id })
        .post({
          body: JSON.stringify(cartPayload),
        })
        .execute()
    ).body

    // offers are supposed to go to sanity
    if (isOffer || isIDMUser || AppSettings.isIdmApp()) {
      const offer = await createSanityEntry(client, updatedCart, customer, values, user, actions, sanityEntryType)

      return offer
    }

    const customerNumber = customer.customerNumber || ""
    const idTranslator = createTranslator("0123456789ABCDEFHIJKLMNOPQRSTUVWXYZ")
    const orderNumber = `${customerNumber}-${idTranslator.generate()}`

    const payload = {
      cart: { typeId: "cart", id: customerCart.id },
      orderNumber,
      version: updatedCart.version,
      ...(storeKey && { storeKey }),
    }

    let order = (
      await commerceApi
        .orders()
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    ).body

    return order
  },
}

/**
 * Creates offer or order on sanity
 * All of the cases for IDM and only offer if user is not IDM
 * Freezes the cart on successful offer create
 *
 * @param {SanityClient} client - Sanity client
 * @param {Object} customerCart - Customer cart
 * @param {Object} customer - Customer
 * @param {Object} values - Values from the form
 * @param {Object} user - User
 * @param {Object} actions - Offer create actions (same as for ct)
 * @param {string} entryType
 *
 * @returns {Promise<Object>} - Offer object
 */
async function createSanityEntry(client, customerCart, customer, values, user, actions, entryType) {
  const createdBy = await getEmployeeReference({
    employeeNumber: user.employeeNumber,
    sanityClient: client,
  })

  const shippingAddress = actions?.find((action) => action.action === "setShippingAddress")?.address
  const customFields = actions?.find((action) => action.action === "setCustomType")?.fields

  delete customFields.customerExternalId
  delete customFields.deliveryObjectDetails
  delete customFields.freightSurcharge
  delete customFields.createdByEmployeeNumber
  delete shippingAddress?.custom
  delete shippingAddress?.externalId

  if (entryType === "offer" && customFields?.reservationNumber) {
    delete customFields.reservationNumber
  }

  if (entryType === "offer" && customFields?.picklistNumber) {
    delete customFields.picklistNumber
  }

  const lineItems = mapLineItems(customerCart.customLineItems)

  const doc = {
    _type: entryType,
    customerNumber: customer.customerNumber,
    commercetoolsCartId: customerCart.id,
    billingAddress: customerCart.billingAddress,
    freightSurcharge: customerCart?.custom?.fields?.freightSurcharge,
    totalGross: customerCart.taxedPrice.totalGross,
    totalNet: customerCart.taxedPrice.totalNet,
    totalTax: customerCart.taxedPrice.totalTax,
    totalPrice: customerCart.totalPrice,
    shippingAddress,
    lineItems,
    createdAt: new Date().toISOString(),
    modificationSource: "Sanity",
    ...(createdBy && { createdBy }),
    ...customFields,
    ...(entryType !== "reservation" && { deliveryObject: values.deliveryObjectValues }),
    shippingMethodName: values.shippingMethodName,
  }

  try {
    const insertOrder = await client.create(doc)
    return { id: insertOrder?._id }
  } catch (error) {
    console.error("Error creating order in Sanity", error)
    return null
  }
}

const mapLineItems = (lineItems) => {
  return lineItems.map((lineItem) => {
    return {
      slug: lineItem.slug,
      id: lineItem.id,
      totalGross: lineItem.taxedPrice.totalGross,
      totalNet: lineItem.taxedPrice.totalNet,
      totalTax: lineItem.taxedPrice.totalTax,
      totalPrice: lineItem.totalPrice,
      position: lineItem.custom.fields?.position,
      priceId: lineItem.custom.fields?.priceId,
      productId: lineItem.custom.fields?.productId,
      sku: lineItem.custom.fields?.sku,
      skuPrice: lineItem.custom.fields?.skuPrice,
      quantity: lineItem.quantity,
      taxRateKey: lineItem.taxRate.key,
      discountPercent: lineItem?.custom?.fields?.discountPercent || 0,
      sendProductText: lineItem?.custom?.fields?.sendProductText || false,
      sendTechnicalManual: lineItem?.custom?.fields?.sendTechnicalManual || false,
      sendCertificateWithOffer: lineItem?.custom?.fields?.sendCertificateWithOffer || false,
      additionalInformation: lineItem?.custom?.fields?.additionalInformation,
      additionalComment: lineItem?.custom?.fields?.additionalComment,
      _key: lineItem.slug,
    }
  })
}
